import { HTMLAttributes, MutableRefObject, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketAccessory, MarketButton, MarketList } from '@market/react';
import {
  ApplePay,
  CashAppPay,
  GooglePay,
  Payments,
  TokenResult,
} from '@square/web-payments-sdk-types';
import { ReactComponent as ApplePayLogo } from 'images/icons/apple-pay-2.svg';
import { ReactComponent as GooglePayLogo } from 'images/icons/google-pay-2.svg';
import {
  attachCashAppPay,
  cashAppPayDivId,
} from 'typescript/utilities/web_payment_sdk';
import styles from 'typescript/apps/checkout_v3/components/payment/payment_methods.module.scss';
import { PaymentType } from 'typescript/constants/enums/payment';
import { PaymentMethod } from 'typescript/apps/checkout_v3/components/payment/use_submit_order';
import { CHECKOUT_PAGE_CLICK_EVENTS } from 'typescript/constants/tracking';
import { onCheckoutPageClick } from 'typescript/utilities/tracking';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { usePaymentContext } from 'typescript/context/payment_context';
import { useSubscriptionDisclosure } from 'typescript/apps/checkout_v3/hooks/use_subscription_disclosure';

export const GPAY_TEST_ID = 'gpay-test-id';
export const APPLE_PAY_TEST_ID = 'apple-pay-test-id';

interface PaymentMethodsProps extends HTMLAttributes<HTMLElement> {
  disabled: boolean;
  submit: (
    paymentMethod: PaymentMethod,
    paymentType: PaymentType,
    tokenResult?: TokenResult,
  ) => Promise<void>;
  sqPayments?: MutableRefObject<Payments>;
  applePay?: ApplePay;
  cashAppPay?: CashAppPay;
  googlePay?: GooglePay;
}

const PaymentMethods = ({
  className,
  disabled,
  submit,
  applePay,
  cashAppPay,
  googlePay,
}: PaymentMethodsProps) => {
  const { t } = useTranslation();
  const { trackingData } = useCheckoutFlow();
  const { subscriptionConsentGiven, setSubscriptionConsentError } =
    usePaymentContext();
  const { consentRequired } = useSubscriptionDisclosure();

  // Block wallets until opt_out consent is given — especially the mobile Cash
  // App Pay redirect, whose return page loses the in-memory consent state.
  const consentBlocked = consentRequired && !subscriptionConsentGiven;

  const blockedForMissingConsent = (): boolean => {
    if (consentBlocked) {
      setSubscriptionConsentError(true);
      return true;
    }
    return false;
  };

  useEffect(() => {
    // Do not attach (render) the Cash App Pay button until consent is given, so
    // the mobile redirect can never be initiated without it.
    if (!cashAppPay || consentBlocked) return;

    // Displays Pay with Cash button
    attachCashAppPay(cashAppPay);

    const handleTokenization = ({
      detail: { tokenResult },
    }: CustomEvent<{ tokenResult: TokenResult }>) => {
      submit(cashAppPay, PaymentType.CASH_APP_PAY, tokenResult);
    };

    // for desktop (user approves via mobile cam), there is no redirect, handle `ontokenization` and call submit/tokenize
    // for mobile, the user will continue through to the cash app page/app and follow the redirectURL we provided
    //             that redirect will handle the call to create the payment, see: `cash_app.redirect.tsx`.
    cashAppPay.addEventListener('ontokenization', handleTokenization);

    // eslint-disable-next-line consistent-return
    return () => {
      cashAppPay.removeEventListener('ontokenization', handleTokenization);
      try {
        cashAppPay.detach();
      } catch {
        // Cash App Pay may not be fully attached yet if the component
        // unmounts before the SDK's async attach completes.
      }
    };
  }, [cashAppPay, consentBlocked]);

  if (!googlePay && !applePay && !cashAppPay) {
    return null;
  }

  return (
    <div className={className}>
      <h3 className={styles.extraGridGap}>
        {t('checkout.payment_v3.payInFull.paymentMethods.title')}
      </h3>
      <MarketList className={styles.paymentMethodsList}>
        {cashAppPay && (
          <div
            id={cashAppPayDivId}
            className={disabled ? styles.disabled : ''}
            onClick={() => {
              onCheckoutPageClick({
                eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.cash_app_pay_button_click,
                trackingData,
              });
              // When consent is missing the SDK button is not attached, so a
              // tap lands on the empty container — point the buyer at the
              // checkbox instead of doing nothing.
              blockedForMissingConsent();
            }}
          />
        )}
        {googlePay && (
          <MarketButton
            data-testid={GPAY_TEST_ID}
            onClick={() => {
              onCheckoutPageClick({
                eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.google_pay_button_click,
                trackingData,
              });
              if (blockedForMissingConsent()) return;
              submit(googlePay, PaymentType.GOOGLE_PAY);
            }}
            disabled={disabled}
            icon-only
            rank="secondary"
          >
            <MarketAccessory slot="icon">
              <GooglePayLogo />
            </MarketAccessory>
          </MarketButton>
        )}
        {applePay && (
          <MarketButton
            data-testid={APPLE_PAY_TEST_ID}
            onClick={() => {
              onCheckoutPageClick({
                eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.apple_pay_button_click,
                trackingData,
              });
              if (blockedForMissingConsent()) return;
              submit(applePay, PaymentType.APPLE_PAY);
            }}
            disabled={disabled}
            icon-only
            rank="secondary"
            className={styles.blackBackground}
          >
            <MarketAccessory slot="icon">
              <ApplePayLogo />
            </MarketAccessory>
          </MarketButton>
        )}
      </MarketList>
    </div>
  );
};

export default PaymentMethods;
